const BASE_LAYER_Z_INDEX = 1050;
const LAYER_STEP = 20;

const activeLayers = [];
let nextLayerId = 1;

function parseZIndex(element) {
    if (!element || typeof window === "undefined") return null;

    const parsedZIndex = Number.parseInt(window.getComputedStyle(element).zIndex, 10);
    return Number.isNaN(parsedZIndex) ? null : parsedZIndex;
}

function isVisible(element) {
    if (!element || typeof window === "undefined") return false;

    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
}

function findHighestUnmanagedOverlayZIndex(ownerElement) {
    if (typeof document === "undefined") return null;

    let highestZIndex = null;

    document.querySelectorAll(".modal, .modal-backdrop").forEach((overlayElement) => {
        if (ownerElement?.contains(overlayElement) || !isVisible(overlayElement)) return;

        const managedLayer = overlayElement.closest("[data-bo-modal-layer]");
        if (managedLayer) return;

        const overlayZIndex = parseZIndex(overlayElement) ?? BASE_LAYER_Z_INDEX;
        highestZIndex = Math.max(highestZIndex ?? overlayZIndex, overlayZIndex);
    });

    return highestZIndex;
}

function getTopLayer() {
    return activeLayers.reduce((topLayer, layer) => {
        if (!topLayer || layer.zIndex > topLayer.zIndex) return layer;
        return topLayer;
    }, null);
}

function refreshAccessibility() {
    const topLayer = getTopLayer();

    activeLayers.forEach((layer) => {
        if (!layer.surfaceElement) return;

        const isTopLayer = layer === topLayer;
        layer.surfaceElement.setAttribute("aria-hidden", isTopLayer ? "false" : "true");

        if (isTopLayer) {
            layer.surfaceElement.setAttribute("aria-modal", "true");
        } else {
            layer.surfaceElement.removeAttribute("aria-modal");
        }
    });
}

function hasVisibleUnmanagedModal() {
    if (typeof document === "undefined") return false;

    return Array.from(document.querySelectorAll(".modal")).some((modalElement) => {
        return !modalElement.closest("[data-bo-modal-layer]") && isVisible(modalElement);
    });
}

function refreshBodyState() {
    if (typeof document === "undefined") return;

    const shouldLockBody = activeLayers.length > 0 || hasVisibleUnmanagedModal();
    document.body.classList.toggle("modal-open", shouldLockBody);
}

function acquire(ownerElement) {
    const existingLayer = activeLayers.find((layer) => layer.ownerElement === ownerElement);
    if (existingLayer) return existingLayer;

    const highestManagedZIndex = activeLayers.reduce((highestZIndex, layer) => {
        return Math.max(highestZIndex, layer.zIndex);
    }, BASE_LAYER_Z_INDEX - LAYER_STEP);
    const highestUnmanagedZIndex = findHighestUnmanagedOverlayZIndex(ownerElement);
    const highestActiveZIndex = Math.max(
        highestManagedZIndex,
        highestUnmanagedZIndex ?? BASE_LAYER_Z_INDEX - LAYER_STEP,
    );
    const zIndex = highestActiveZIndex < BASE_LAYER_Z_INDEX
        ? BASE_LAYER_Z_INDEX
        : highestActiveZIndex + LAYER_STEP;

    const layer = {
        id: nextLayerId++,
        ownerElement,
        previousFocus: typeof document !== "undefined" ? document.activeElement : null,
        surfaceElement: null,
        zIndex,
        closing: false,
    };

    activeLayers.push(layer);
    refreshBodyState();
    return layer;
}

function setSurface(layer, surfaceElement) {
    if (!layer || !activeLayers.includes(layer)) return;

    layer.surfaceElement = surfaceElement;
    refreshAccessibility();
}

function beginClose(layer) {
    if (!layer || !activeLayers.includes(layer)) return;
    layer.closing = true;
}

function release(layer) {
    if (!layer) return;

    const layerIndex = activeLayers.indexOf(layer);
    if (layerIndex === -1) return;

    activeLayers.splice(layerIndex, 1);
    refreshAccessibility();
    refreshBodyState();

    const topLayer = getTopLayer();
    const previousFocusIsInTopLayer = Boolean(
        topLayer?.surfaceElement
        && layer.previousFocus
        && topLayer.surfaceElement.contains(layer.previousFocus),
    );
    const focusTarget = previousFocusIsInTopLayer
        ? layer.previousFocus
        : topLayer?.surfaceElement ?? layer.previousFocus;
    if (focusTarget && document.contains(focusTarget) && typeof focusTarget.focus === "function") {
        focusTarget.focus();
    }
}

function isTop(layer) {
    return Boolean(layer) && getTopLayer() === layer;
}

export default {
    acquire,
    beginClose,
    isTop,
    release,
    setSurface,
};
